import { initializeApp } from "firebase/app";
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  memoryLocalCache,
  connectFirestoreEmulator,
  disableNetwork,
  enableNetwork,
} from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { withTimeout } from "./utils/withTimeout";

// Firebase configuration for clozesuretools project
const firebaseConfig = {
  apiKey: "AIzaSyDkyrlSr76FjoQc2922LeBaTiY_jVfQQLI",
  authDomain: "clozesuretools.firebaseapp.com",
  projectId: "clozesuretools",
  storageBucket: "clozesuretools.firebasestorage.app",
  messagingSenderId: "1074885904295",
  appId: "1:1074885904295:web:a5def01d9907b02f97d882",
};

// Initialize Firebase *once*
const app = initializeApp(firebaseConfig);

// Auth instance. We pin persistence to localStorage (browserLocalPersistence)
// instead of the default IndexedDB store: on Safari, reloading a tab can leave
// the previous page instance holding the auth IndexedDB store, so the new
// page's auth init never completes → onIdTokenChanged never fires → the
// "Checking authentication…" route guard spins forever (the long-standing
// "reload hangs, close the window to fix it" bug). localStorage is synchronous
// and never stalls that way. initializeAuth must run before any getAuth(app);
// the try/catch keeps HMR / double-import safe.
let auth;
try {
  auth = initializeAuth(app, { persistence: browserLocalPersistence });
} catch (e) {
  auth = getAuth(app);
}

// Check if we're using emulators (before initializing Firestore)
// Set REACT_APP_USE_EMULATORS=true to use local emulators
// Otherwise, will use production even in development mode
const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';

// Initialize Firestore with custom settings to avoid IndexedDB issues
// Disable persistent cache in emulator mode to avoid conflicts
// Note: Long polling is needed for production (proxy/firewall issues) but not for local emulator
/**
 * ⚠️ SAFARI GETS A MEMORY CACHE (10 Sep 2026).
 *
 * Firestore runs every operation through ONE sequential queue, and with the
 * on-disk cache that queue waits on IndexedDB. Safari's IndexedDB can hang —
 * not fail, hang — and then everything behind it waits forever: listeners get
 * no first snapshot, and even disableNetwork/enableNetwork never return.
 * Observed as the Scrapes list "not answering, even after reconnecting" after
 * visiting a scrape and a property (pages that pull hundreds of documents into
 * the cache), across several tabs at once, and cured only by force-quitting
 * Safari. The earlier multi-tab deadlock (below) was the same engine.
 *
 * A memory cache never touches IndexedDB. The cost is a server round trip on
 * reload instead of an instant cached paint — trivial for this tool. Chrome
 * and Firefox keep the on-disk cache. `firestoreCacheKind` is reported in the
 * Scrapes tab's "Copy details", so a recurrence says which engine it was on.
 */
const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);
const firestoreCacheKind = useEmulators ? "emulator" : isSafari ? "memory" : "persistent";

const db = useEmulators
  ? initializeFirestore(app, {
      // Use WebSockets for emulator (faster, more reliable with large datasets)
    })
  : initializeFirestore(app, {
      // Single-tab manager keeps the on-disk cache (fast warm reloads) but drops the
      // multi-tab leader-election that deadlocks IndexedDB under Safari and hangs the app
      // on refresh. Matched by the non-blocking auth gate in src/auth/AuthContext.js.
      localCache: isSafari
        ? memoryLocalCache()
        : persistentLocalCache({
            tabManager: persistentSingleTabManager(),
          }),
      // Auto-detect the transport: use the efficient multiplexed WebChannel when
      // the network allows it (verified working here — the same app on the same
      // network is hang-free in Chrome, which uses WebChannel), and fall back to
      // long-polling only when a proxy actually requires it.
      //
      // We previously FORCED long-polling to dodge repeated /channel 400s. But
      // forced long-polling holds a separate HTTP connection open per listener,
      // and under Safari's single shared NetworkProcess those pile up — after the
      // tab idles the held connections go stale and the WHOLE browser hangs until
      // a full reload (fresh window = fast, then degrades after idle). Auto-detect
      // keeps far fewer connections open and recovers from idle, which fixes that.
      //
      // ⚠️ REVERT: if the /channel 400s ("RPC stream transport errored" / stalled
      // reads on a proxied network) return, switch this single line back to
      //   experimentalForceLongPolling: true
      experimentalAutoDetectLongPolling: true,
    });

// Get Functions instance (europe-west2 region)
const functions = getFunctions(app, "europe-west2");

// ⭐ EMULATOR CONFIGURATION - Connect to local emulators for development
if (useEmulators) {
  // Connect Auth Emulator (port 9099)
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  
  // Connect Firestore Emulator (port 8080)
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  
  // Connect Functions Emulator (port 5001)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

}

/**
 * Restart Firestore's streams without reloading the page.
 *
 * A listener whose connection has gone quiet does not error — it simply stops
 * delivering, and until now the only cure was a full reload. Cycling the
 * network restarts every stream; pending writes are kept and flushed.
 */
async function reconnectFirestore(timeoutMs = 5000) {
  // Bounded: if the client's queue is blocked (see the Safari note above),
  // these never settle, and an unbounded await would hang the caller too —
  // and hide the one fact that tells a blocked client from a quiet network.
  const blocked = (step) =>
    `${step} did not complete within ${timeoutMs / 1000}s — the database client in this tab is blocked`;
  await withTimeout(disableNetwork(db), timeoutMs, blocked("disableNetwork"));
  await withTimeout(enableNetwork(db), timeoutMs, blocked("enableNetwork"));
}

export { app, auth, db, functions, useEmulators, reconnectFirestore, firestoreCacheKind };
